// Dev-session bridge for the dock (components/AgentDock.js): follows the
// Dev Console's open session (or the one /voice/session named) and tells
// the voice what happened as `DEV …` system items, the way the copilot
// follow does for the copilot. Level from `voice.devNarration`:
//   quiet      — nothing
//   milestones — plan ready, questions, PR opened, ship step done/failed,
//                errors, checks failed
//   verbose    — milestones + the agent's progress remarks (throttled)
// Each fact is told once per voice session; nothing older than the voice
// session start is narrated (the reconnect DEV CONTEXT covers history).
import { useEffect, useRef } from 'react';
import { useDoc, useOrdered, useActiveDevSession } from './liveDocs';

const VERBOSE_GAP_MS = 20_000;
const clip = (s, n) => String(s || '').replace(/\s+/g, ' ').trim().slice(0, n);

export default function useDevNarration({ voiceActive, sendVoiceSystem, narrationRef, followRef, sessionStartRef }) {
  const active = useActiveDevSession();
  const level = voiceActive ? (narrationRef.current || 'quiet') : 'quiet';
  const follow = level === 'quiet' ? null : (active || followRef.current || null);
  const sess = useDoc(follow ? `devSessions/${follow}` : null);
  const events = useOrdered(follow ? `devSessions/${follow}/events` : null, 'seq', 'desc', 30);
  const told = useRef(new Set());
  const lastVerboseAt = useRef(0);

  // A fresh voice session starts with a clean slate.
  useEffect(() => { if (!voiceActive) { told.current = new Set(); lastVerboseAt.current = 0; } }, [voiceActive]);

  // Events: plan / questions / result / error / (verbose) status + assistant.
  useEffect(() => {
    if (!follow || level === 'quiet' || !events.length) return;
    const since = sessionStartRef.current || 0;
    const tell = (key, text, respond = true) => {
      if (told.current.has(key)) return;
      if (!sendVoiceSystem(text, { respond })) return; // socket not ready: retry on next render
      told.current.add(key);
    };
    const title = clip(sess?.title || 'the dev session', 80);
    for (const e of [...events].reverse()) {
      if (!e || (e.ts || 0) < since) continue;
      const key = `${follow}:${e.seq}`;
      if (e.type === 'plan') {
        tell(key, `DEV PLAN READY for "${title}": ${clip(e.text, 900)} — needs approval (say "approve the plan" or ask for changes).`);
      } else if (e.type === 'questions' && Array.isArray(e.questions)) {
        const qs = e.questions.slice(0, 4).map((q, i) => `${i + 1}. ${clip(q.question, 200)}${q.options?.length ? ` (options: ${q.options.slice(0, 5).map((o) => clip(o, 60)).join(', ')})` : ''}`).join(' ');
        tell(key, `DEV QUESTIONS from the agent on "${title}" (relay them, collect answers, then answer the session): ${qs}`);
      } else if (e.type === 'result' && e.status === 'error') {
        tell(key, `DEV ERROR on "${title}": ${clip(e.error, 400)}. Offer a follow-up or a retry.`);
      } else if (e.type === 'result' && /pre-push checks still fail/i.test(e.text || '')) {
        tell(key, `DEV CHECKS FAILED on "${title}": ${clip(e.text, 400)}`);
      } else if (level === 'verbose' && (e.type === 'status' || e.type === 'assistant')) {
        const now = Date.now();
        if (now - lastVerboseAt.current < VERBOSE_GAP_MS) continue;
        const text = e.type === 'assistant' ? clip(e.text, 500) : clip(e.message, 240);
        if (!text) continue;
        lastVerboseAt.current = now;
        tell(key, `DEV ${e.type === 'assistant' ? 'AGENT SAID' : 'PROGRESS'} (${title}): ${text}. One short sentence, or stay quiet if the person is talking.`, false);
      }
    }
  }, [events, sess, follow, level, sendVoiceSystem, sessionStartRef]);

  // Session doc: PR opened, ship steps settling, waiting-on-you states.
  useEffect(() => {
    if (!follow || level === 'quiet' || !sess) return;
    const since = sessionStartRef.current || 0;
    if ((sess.updatedAt || 0) < since) return;
    const tell = (key, text) => {
      if (told.current.has(key)) return;
      if (!sendVoiceSystem(text, { respond: true })) return;
      told.current.add(key);
    };
    const title = clip(sess.title || 'the dev session', 80);
    if (sess.prUrl) {
      const num = (String(sess.prUrl).match(/\/(\d+)\/?$/) || [])[1];
      tell(`${follow}:pr`, `DEV PR OPENED for "${title}"${num ? ` (#${num})` : ''}. Ship from here when ready.`);
    }
    const n = sess.shipNext;
    if (n?.stepId) {
      if (n.action === 'done') tell(`${follow}:ship:${n.stepId}:${n.env || ''}:done`, `DEV SHIP done — ${clip(n.label, 200)}.`);
      else if (n.action === 'retry' || n.action === 'blocked') tell(`${follow}:ship:${n.stepId}:${n.env || ''}:${n.action}:${sess.shipAt || ''}`, `DEV SHIP ${n.action === 'blocked' ? 'blocked' : 'failed'} — ${clip(n.label, 200)}.`);
      else if (['ship', 'kitdev', 'release', 'approve'].includes(n.action) && !sess.shipRunning) tell(`${follow}:ship:${n.stepId}:${n.env || ''}:${n.action}`, `DEV NEXT STEP waiting on you — ${clip(n.label, 200)}. Say the word to go.`);
    }
  }, [sess, follow, level, sendVoiceSystem, sessionStartRef]);
}
